import React from 'react';
import { Button } from '../ui/Button';
import { ANSWER_LABEL_CLASS } from '../../constants/timelineCard';
import {
  ADD_TO_KNOWLEDGE_BASE_LABEL,
  INQUIRY_PROCESSED_LABEL,
  MARK_INQUIRY_PROCESSED_LABEL,
} from '../../constants/labels';

/**
 * A detected objection / FAQ / New Inquiry from a Playbook Live session: the
 * question card the rep saw, with the stored knowledge-base answer and the AI
 * answer. Mirrors the post-call timeline's detection card (SessionDetail);
 * a New Inquiry carries the same admin actions (add to the knowledge base, or
 * dismiss) when `inquiryActions` is given.
 */
export interface TrainingQuestionDetection {
  question?: string;
  category?: string;
  relevance_score?: number;
  stored_answer?: string | null;
  answer?: string | null;
  timestamp: string;
  isProcessed?: boolean;
}

export interface InquiryCardActions {
  isProcessed: boolean;
  isSaving: boolean;
  onDismiss: () => void;
  onAddToKnowledgeBase: () => void;
}

const NEW_INQUIRY_CATEGORY = 'New Inquiry';
const PERCENT = 100;
const BULLET_PATTERN = /^([•\-*]|\d+\.)\s*/;

const COPY = {
  newInquiry: 'New Objection/FAQ',
  relevance: 'Relevance',
  storedAnswer: 'Stored answer (knowledge base):',
  aiAnswer: 'AI generated answer:',
  noAiAnswer: 'No AI answer was generated for this question.',
} as const;

/** Answer text as lines, with leading bullets or numbers drawn as bullets. */
const AnswerText: React.FC<{ text: string }> = ({ text }) => (
  <div className="space-y-1">
    {text.split('\n').map((line) => line.trim()).filter(Boolean).map((line, index) => (
      BULLET_PATTERN.test(line) ? (
        <div key={index} className="flex items-start gap-2">
          <span className="text-ink-muted mt-0.5">•</span>
          <span className="flex-1">{line.replace(BULLET_PATTERN, '')}</span>
        </div>
      ) : (
        <div key={index}>{line}</div>
      )
    ))}
  </div>
);

const InquiryActions: React.FC<{ actions: InquiryCardActions }> = ({ actions }) => {
  if (actions.isProcessed) {
    return (
      <span className="px-2 py-1 text-xs font-medium text-success bg-success-tint rounded-full">
        {INQUIRY_PROCESSED_LABEL}
      </span>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <Button variant="secondary" size="sm" onClick={actions.onDismiss} disabled={actions.isSaving}>
        {MARK_INQUIRY_PROCESSED_LABEL}
      </Button>
      <Button variant="primary" size="sm" onClick={actions.onAddToKnowledgeBase} disabled={actions.isSaving}>
        {ADD_TO_KNOWLEDGE_BASE_LABEL}
      </Button>
    </div>
  );
};

export const TrainingDetectionCard: React.FC<{
  detection: TrainingQuestionDetection;
  timeLabel: string;
  inquiryActions?: InquiryCardActions;
}> = ({ detection, timeLabel, inquiryActions }) => {
  const isNewInquiry = detection?.category === NEW_INQUIRY_CATEGORY;
  const relevance = detection?.relevance_score;
  return (
    <div className="p-3 rounded-lg bg-surface-subtle border-l-4 border-ink-border" data-testid="training-detection-card">
      <div className="flex justify-between items-start mb-1">
        <div className="flex items-center gap-2">
          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-surface-sunken text-ink-secondary">
            {isNewInquiry ? COPY.newInquiry : detection?.category}
          </span>
          {!isNewInquiry && typeof relevance === 'number' && (
            <span className="text-xs text-ink-muted">
              {COPY.relevance}: {(relevance * PERCENT).toFixed(0)}%
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isNewInquiry && inquiryActions && <InquiryActions actions={inquiryActions} />}
          <span className="text-xs text-ink-muted">{timeLabel}</span>
        </div>
      </div>
      <div className="text-sm font-medium">{detection?.question}</div>
      <div className="mt-2 text-sm text-ink-secondary space-y-3">
        {detection?.stored_answer && (
          <div>
            <div className={ANSWER_LABEL_CLASS}>{COPY.storedAnswer}</div>
            <div className="bg-surface-subtle p-2 rounded border border-hairline">
              <AnswerText text={detection.stored_answer} />
            </div>
          </div>
        )}
        {detection?.answer ? (
          <div>
            <div className={ANSWER_LABEL_CLASS}>{COPY.aiAnswer}</div>
            <div className="bg-info-tint p-2 rounded border border-info">
              <AnswerText text={detection.answer} />
            </div>
          </div>
        ) : (
          <div className="text-xs text-ink-muted italic">{COPY.noAiAnswer}</div>
        )}
      </div>
    </div>
  );
};

export default TrainingDetectionCard;
